// ── PRF RENEWAL QUOTE — next year's structure, priced against next year's tables
//
// A renewal starts from what the grower has: last year's acres, intervals and
// percentages. It is NOT last year's quote with a new date on it, because the
// two crop years are not the same shape.
//
// ⚠ THE PRACTICE BAND MOVES THE COUNTY BASE VALUE ON **BOTH** CROP YEARS.
// Measured 2026-09-02 on grid 27808, GENESEE (037), HAYING (030), APRMAY — the
// two years are identical in shape and each publishes SIX bands over TWO tiers:
//
//     band 428 / 468 / 488  ->  county base value $330
//     band 528 / 568 / 588  ->  county base value $447
//
// That is a 35% swing in protection before a rate is read. An earlier version of
// this header said CY2026 carried no practice code at all; that is true of
// `prf_premium_rates` — where CY2026 is NULL and CY2027 is banded, which is what
// `prfRateLookup` documents — and it is NOT true of `prf_county_base_values`,
// which is banded on both. Carrying the rate claim across to the CBV was an
// inference that fit one table and was wrong about the other.
//
// So the band is never defaulted. But it does not have to be GUESSED either: the
// review page resolves last year's CBV from the AIP's own liability, and that is
// a sourced figure. `familiesMatchingCbv` carries THAT value forward and keeps
// only the bands consistent with it; `quoteAcrossBands` then prices every
// survivor and answers only when they agree. Several bands landing on one
// quote is not ambiguity — it is the same product under several codes.
//
// Everything monetary goes through the shipped `quotePrf`; the rate goes through
// the shipped `lookupPrfRate`. Nothing is re-derived here.

const { quotePrf, PRF_SUBSIDY, IV_ORDER } = require("./prfQuote.js");
const { USE_TO_TYPE } = require("./prfActuarial.js");

// "HAYING" -> "030". The block carries the USE; bandFamilies needs the TYPE, and a
// null type means "every type" to that filter — which is how grazing's band came to
// be offered on a haying quote. Unknown stays null: a wrong scope is worse than none.
function typeForUse(use) {
  if (use == null) return null;
  return USE_TO_TYPE[String(use).trim().toUpperCase()] || null;
}

// RMA encodes the practice band and the interval in ONE number — band 425 plus
// MAYJUN's position (4) is 429. The FAMILY is what a grower elects; the
// per-interval code is derived from it. Deriving it is what lets one election
// price all eleven intervals.
function familyOf(practiceCode, intervalCode) {
  // ⚠ `Number(null)` IS 0, NOT NaN, and `Number("")` is 0 too — so a Number.isFinite
  // guard alone lets a row with NO practice code resolve to a real family (0 minus
  // the interval index, a negative band nobody publishes). That is the CY2026
  // shape, and it would have made every unbanded row answer a banded question.
  // This repo has paid for this exact coercion once already, on the LRP weight
  // band, where it read as a 0–0 cwt class and excluded every animal.
  if (practiceCode == null || practiceCode === "") return null;
  const p = Number(practiceCode);
  const i = IV_ORDER.indexOf(intervalCode);
  if (!Number.isFinite(p) || i < 0) return null;
  return p - i;
}
function practiceFor(family, intervalCode) {
  const i = IV_ORDER.indexOf(intervalCode);
  if (family == null || i < 0) return null;
  return String(Number(family) + i);
}

// ── WHAT A BAND IS ──────────────────────────────────────────────────────────
//
// Kevin, 2026-09-02: "what are those bands? 425xx". They rendered as raw codes,
// which follows this repo's convention for a code with no established meaning —
// but the meaning IS establishable, and a picker that decides 35% of a grower's
// protection should not be a row of numbers.
//
// SOURCED FROM RMA'S OWN PRF DECISION SUPPORT TOOL, which takes irrigation and
// organic as SEPARATE inputs and prices a county base value from them. Probed
// 2026-09-02 against PrfExternalPricingRates/GetPricingRates, holding grid 27808 /
// NY / GENESEE / HAYING / APRMAY constant and varying only those two:
//
//     irrigationPracticeCode 003 (non-irrigated) -> county base value $447
//     irrigationPracticeCode 002 (irrigated)     -> county base value $330
//     organicPracticeCode 997 / 001 / 002        -> NO CHANGE, either way
//
// and those are exactly the two tiers the stored actuarial carries — 425/465/485
// at $330, 525/565/585 at $447. Repeated on WYOMING grid 27509: 447 vs 329, same
// direction. Two cells, so it is written down as measured on NY haying rather than
// as a national rule.
//
// ⚠ NON-IRRIGATED IS THE **HIGHER** TIER, WHICH INVERTS THE OBVIOUS GUESS. Anyone
// reasoning from "irrigated ground is worth more" would have labelled these
// backwards and been confident about it. That is why this was probed.
//
// ⚠ AND THE THREE CODES WITHIN A TIER ARE NOT DISTINGUISHED HERE. They differ by
// organic status, and measured on grid 27808 they carry the SAME county base value
// AND the same premium rate (0.0786 at 85%, 0.1001 at 90% on all six) — so the
// choice within a tier moves no number. WHICH of the three is conventional,
// certified or transitional is NOT established from anything we hold, so it is not
// claimed. The tier is what moves the money and the tier is what is labelled.
const BAND_PRACTICE = {
  425: "Irrigated", 465: "Irrigated", 485: "Irrigated",
  525: "Non-irrigated", 565: "Non-irrigated", 585: "Non-irrigated",
};
// A family this table does not name renders RAW — the same convention the claim
// stage codes and the corn type codes follow. Never a guess, never a default.
function bandLabel(family) {
  const f = Number(family);
  return Number.isFinite(f) && BAND_PRACTICE[f] ? BAND_PRACTICE[f] : null;
}

// The bands RMA publishes for this grid/county/type, as families. Sorted so the
// picker is stable between renders; a set that changes order changes which one
// looks "first" and invites a caller to treat it as a default.
function bandFamilies(cbvRows, { countyCode = null, typeCode = null } = {}) {
  const fams = new Set();
  for (const r of cbvRows || []) {
    if (countyCode != null && String(r.county_code) !== String(countyCode)) continue;
    if (typeCode != null && String(r.type_code) !== String(typeCode)) continue;
    const f = familyOf(r.practice_code, r.interval_code);
    if (f != null) fams.add(f);
  }
  return [...fams].sort((a, b) => a - b);
}

// ── SIX BANDS ARE TWO CHOICES ──────────────────────────────────────────
//
// Kevin, 2026-09-03, on a row of six chips reading "Irrigated · band 425xx",
// "Non-irrigated · band 525xx · $447/ac" and so on: "This needs to be
// simplified." He is right, and the reason it CAN be simplified is measured
// rather than assumed — within a tier the three codes differ only by organic
// status and carry the same county base value and the same rate, so the choice
// between them moves no number. A picker offering six options where the data
// holds two makes the agent decide something that does not matter, which is how
// they learn to click past the one that does.
//
// ⚠ SO THE COLLAPSE IS VERIFIED PER RENDER, NEVER TRUSTED. Where a tier's
// members DISAGREE on the county base value it is not one choice, and the group
// stays split so the question still gets asked. A tier whose members cannot be
// priced at all says so on its own button instead of showing a bare label with
// no dollar figure beside it — which is what the six-chip row was doing.
function practiceChoices(cbvRows, sel, intervals) {
  const out = [];
  const byLabel = new Map();
  for (const f of bandFamilies(cbvRows, sel)) {
    // A family this table cannot name is its own choice, rendered raw — the
    // convention the claim stage codes and corn type codes already follow.
    const key = bandLabel(f) || `band ${f}xx`;
    if (!byLabel.has(key)) byLabel.set(key, []);
    byLabel.get(key).push(f);
  }
  for (const [label, fams] of byLabel) {
    const priced = fams.map((f) => ({ f, res: cbvForElection(cbvRows, { ...sel, family: f }, intervals) }));
    const ok = priced.filter((p) => p.res.ok);
    if (!ok.length) {
      out.push({ label, families: fams, cbv: null, collapsed: fams.length > 1,
                 unpriceable: true, reason: priced[0]?.res.reason || "CBV_NOT_SINGLE" });
      continue;
    }
    const first = ok[0].res.cbv;
    const agree = ok.every((p) => Math.abs(p.res.cbv - first) < 0.005);
    if (agree) {
      out.push({ label, families: ok.map((p) => p.f), cbv: first,
                 collapsed: ok.length > 1, unpriceable: false,
                 unresolved: fams.length - ok.length });
    } else {
      // not one product — keep them apart so the agent is still asked
      for (const p of ok) {
        out.push({ label: `${label} · band ${p.f}xx`, families: [p.f], cbv: p.res.cbv,
                   collapsed: false, unpriceable: false, split: true });
      }
    }
  }
  // Highest county base value first — that is the order an agent reads a price
  // list in, and it is stable because the value is a number rather than a code.
  return out.sort((a, b) => (b.cbv ?? -1) - (a.cbv ?? -1));
}

// Counties and types present, so the UI narrows from the DATA rather than from a
// list somebody typed — a county RMA stops publishing disappears on its own.
function cbvOptions(cbvRows) {
  const counties = new Set(), types = new Set();
  for (const r of cbvRows || []) {
    if (r.county_code != null) counties.add(String(r.county_code));
    if (r.type_code != null) types.add(String(r.type_code));
  }
  return { counties: [...counties].sort(), types: [...types].sort() };
}

// ONE published county base value, or nothing. Never a first row, never a max —
// the same contract the rate lookup follows, because a CBV that is not
// identified is not a CBV.
function narrowCbv(cbvRows, { countyCode, typeCode, family, intervalCode }) {
  const want = family == null ? null : practiceFor(family, intervalCode);
  const hits = (cbvRows || []).filter((r) =>
    String(r.interval_code) === String(intervalCode)
    && (countyCode == null || String(r.county_code) === String(countyCode))
    && (typeCode == null || String(r.type_code) === String(typeCode))
    // A NULL practice_code is CY2026's shape and matches only when no band was
    // asked for. Treating null as a wildcard would let a CY2026 row answer a
    // CY2027 question.
    && (want == null ? r.practice_code == null || r.practice_code === "" : String(r.practice_code) === want));
  const vals = [...new Set(hits.map((r) => Number(r.county_base_value)).filter(Number.isFinite))];
  return vals.length === 1 ? vals[0] : null;
}

// ⚠ THE ELECTED INTERVALS MUST SHARE ONE CBV. A band whose intervals carry
// different county base values is not one product, and averaging them would
// invent a price for something RMA does not sell.
function cbvForElection(cbvRows, sel, intervals) {
  const vals = intervals.map((iv) => narrowCbv(cbvRows, { ...sel, intervalCode: iv }));
  if (vals.some((v) => v == null)) return { ok: false, reason: "CBV_NOT_SINGLE" };
  const first = vals[0];
  if (vals.some((v) => Math.abs(v - first) > 1e-9)) {
    return { ok: false, reason: "CBV_DIFFERS_BY_INTERVAL", values: vals };
  }
  return { ok: true, cbv: first };
}

// ── the quote ──────────────────────────────────────────────────────────
// `lookupRate` is injected rather than imported so this module stays free of the
// ESM/CJS boundary and the caller cannot end up with a SECOND rate rule. It is
// always the shipped lookupPrfRate.
function quoteRenewal({
  acres, coverage, productivity, allocation,
  cbvRows, rateRows, lookupRate,
  countyCode = null, typeCode = null, family = null,
  limits = null, share = 1, bfrBonus = 0,
} = {}) {
  const elected = Object.entries(allocation || {}).filter(([, p]) => Number(p) > 0);
  if (!elected.length) return { ok: false, error: "NO_INTERVALS" };
  // ⚠ ACRES ARE NEVER DEFAULTED. A blank box is a question, not a zero, and a
  // zero-acre quote reads as "this is what it costs" rather than "you have not
  // said how many acres". Seeding from last year happens in the CALLER, where
  // the figure has a provenance the grower can be told about.
  if (!(Number(acres) > 0)) return { ok: false, error: "NO_ACRES" };
  if (typeof lookupRate !== "function") return { ok: false, error: "NO_RATE_RULE" };

  const sel = { countyCode, typeCode, family };
  const cbvRes = cbvForElection(cbvRows, sel, elected.map(([iv]) => iv));
  if (!cbvRes.ok) return { ok: false, error: cbvRes.reason, values: cbvRes.values };

  // ⚠ NARROW ONLY AS FAR AS THE DATA REQUIRES. The band identifies the county
  // base value on BOTH crop years, but it identifies the RATE only on CY2027 —
  // `prf_premium_rates` carries a NULL practice_code throughout CY2026. Passing
  // the band to a CY2026 rate lookup matches nothing and the whole baseline
  // refuses, which is what emptied the year-over-year comparison the first time
  // this ran. So: ask without the band first, and only if that is AMBIGUOUS add
  // it. This never picks between disagreeing rates — lookupPrfRate still
  // refuses — it just does not demand a dimension the year does not publish.
  const rates = {}, unresolved = [];
  for (const [iv] of elected) {
    const base = { intervalCode: iv, coverage: Number(coverage), typeCode, countyCode };
    let look = lookupRate(rateRows || [], base);
    if (!look || look.rate == null) {
      const prac = practiceFor(family, iv);
      if (prac != null) look = lookupRate(rateRows || [], { ...base, practiceCode: prac });
    }
    if (look && look.rate != null) rates[iv] = look.rate;
    else unresolved.push(iv);
  }
  if (unresolved.length) return { ok: false, error: "RATE_UNRESOLVED", intervals: unresolved };

  const alloc = {};
  for (const [iv, p] of elected) alloc[iv] = Number(p);
  let q;
  try {
    q = quotePrf({
      countyBaseValue: cbvRes.cbv, coverageLevel: Number(coverage),
      productivityFactor: Number(productivity), acres: Number(acres), share,
      allocation: alloc, rates, subsidyRate: PRF_SUBSIDY[Number(coverage)],
      // Beginning farmer and rancher points (MGR-25-006), added by the engine.
      bfrBonus: Number(bfrBonus) || 0,
      rules: limits || undefined,
    });
  } catch (e) { return { ok: false, error: "QUOTE_REFUSED", message: e.message }; }

  return {
    ok: true,
    cbv: cbvRes.cbv, rates, acres: Number(acres),
    protection: q.total_protection,
    producer_premium: q.producer_premium,
    total_premium: q.total_premium,
    bfr_bonus: q.bfr_bonus,
    subsidy: q.total_subsidy,
    protection_per_acre: q.total_protection / Number(acres),
    // The per-interval percent-of-value limits this quote was validated
    // against, passed through so a caller can OFFER an allocation without
    // inventing a second idea of what RMA allows. Undefined when the
    // actuarials did not supply them, which the engine also treats as
    // 'maximum not enforced'.
    limits: limits || null,
    premium_per_acre: q.producer_premium / Number(acres),
    quote: q,
  };
}

// ── LAST YEAR IS WHAT THE AIP BILLED, NOT WHAT WE CAN RE-PRICE ─────────
//
// The first cut baselined the comparison by re-pricing the policy year through
// the same engine as the quote. That is elegant and it fails, because
// `prf_premium_rates` is SPARSE on the policy year: grid 27509 carries 55 CY2026
// rows against 1,320 for CY2027, so several (county, interval, coverage) cells
// simply are not published and the baseline refuses. On the printed sheet that
// showed as a rate column of em-dashes and NO comparison at all — the one thing
// the page exists to answer.
//
// The AIP's own figures have no such gap and are the better answer anyway:
// "what did PRF cost last year" is what the carrier charged, not our
// re-derivation of it. `prf_intervals` stores liability and gross premium per
// interval, and summing them reproduces the policy header exactly — measured
// 2026-09-02, liability 108,316 = stored 108,316 and gross 9,564 = stored 9,564
// on MP-0924525.
//
// ⚠ THE PRODUCER SHARE IS DERIVED, AND THE SHEET SAYS SO. There is no stored
// producer premium per BLOCK — only per policy — so it is gross x (1 - RMA's
// published subsidy). That reconciles to the AIP's own policy net to the dollar
// (MP-0924528: 7,122 derived against 7,122 stored; MP-0924525: 4,304 against
// 4,303, whole-dollar rounding), which is what licenses using it per block.
// ⚠ AND A BEGINNING FARMER'S SUBSIDY IS HIGHER. `bfrBonus` is the BFR points
// read off the policy's own billed subsidy (prfQuote.bfrFromBilled, MGR-25-006);
// without it the baseline read MP-0930254 at $887 against $616 billed.
function storedBaseline({ rows, coverage, acres, cbv = null, bfrBonus = 0 } = {}) {
  if (!Array.isArray(rows) || !rows.length) return { ok: false, error: "NO_STORED_ROWS" };
  if (!(Number(acres) > 0)) return { ok: false, error: "NO_ACRES" };
  const sub = PRF_SUBSIDY[Number(coverage)];
  if (!(sub > 0 && sub < 1)) return { ok: false, error: "NO_SUBSIDY_RATE" };
  const subAll = sub + (Number(bfrBonus) || 0);
  let protection = 0, gross = 0;
  for (const r of rows) {
    // ⚠ NEVER A PARTIAL SUM, AND `Number(null)` IS 0 RATHER THAN NaN. A finite
    // check alone lets a null stored premium through as ZERO, so the total reads
    // LOW — which prints as a cheaper last year and a renewal that looks like an
    // increase. That is the third time this coercion has bitten in this file;
    // the emptiness is tested for before the number is taken.
    const blank = (v) => v == null || v === "";
    if (blank(r.liability) || blank(r.premium)) return { ok: false, error: "INCOMPLETE_STORED" };
    const l = Number(r.liability), g = Number(r.premium);
    if (!Number.isFinite(l) || !Number.isFinite(g)) return { ok: false, error: "INCOMPLETE_STORED" };
    protection += l; gross += g;
  }
  const producer = gross * (1 - subAll);
  return {
    ok: true, source: "as_billed", acres: Number(acres),
    cbv: Number.isFinite(Number(cbv)) ? Number(cbv) : null,
    protection, total_premium: gross, producer_premium: producer,
    protection_per_acre: protection / Number(acres),
    premium_per_acre: producer / Number(acres),
    subsidy_rate: subAll,
  };
}

// ── the renewal comparison ─────────────────────────────────────────────
// ⚠ PER ACRE IS THE COMPARABLE FIGURE; TOTALS ARE NOT WHEN THE ACRES MOVE.
// Changing acres and comparing total premium mixes a rate change with an
// acreage change and reads as though the product got dearer. So every row
// declares whether it is comparable, and the acreage change is stated rather
// than absorbed.
function compareYears(last, next) {
  const rows = [];
  const acresChanged = last && next && Math.abs(Number(last.acres) - Number(next.acres)) > 1e-9;
  // ⚠ `cents` IS NOT COSMETIC. A per-acre premium rounded to whole dollars reads
  // "$23 → $22, change −$2", which does not add up on its face and makes the
  // reader distrust the whole table. Per-acre figures are small enough that the
  // cents ARE the change; totals are not.
  const push = (label, a, b, unit, comparable = true, cents = false) => {
    const d = (a != null && b != null) ? b - a : null;
    rows.push({ label, last: a, next: b, delta: d,
      pct: (d != null && a) ? d / Math.abs(a) : null, unit, comparable, cents });
  };
  push("Coverage $/acre", last?.protection_per_acre, next?.protection_per_acre, "$", true, true);
  push("Producer premium $/acre", last?.premium_per_acre, next?.premium_per_acre, "$", true, true);
  push("County base value $/acre", last?.cbv, next?.cbv, "$");
  push("Acres", last?.acres, next?.acres, "ac", true, true);
  push("Total protection", last?.protection, next?.protection, "$", !acresChanged);
  push("Total producer premium", last?.producer_premium, next?.producer_premium, "$", !acresChanged);
  return { rows, acres_changed: acresChanged };
}

// ── carrying last year's SOURCED county base value forward ─────────────
// The review page solves last year's CBV out of the AIP's own liability, so it
// is a stated figure. Keeping only the bands consistent with it is inheritance
// from a source; it is NOT choosing a band, and where the grower has genuinely
// changed practice the caller can still override.
function familiesMatchingCbv(cbvRows, sel, intervals, targetCbv) {
  if (!(Number(targetCbv) > 0)) return [];
  return bandFamilies(cbvRows, sel).filter((f) => {
    const res = cbvForElection(cbvRows, { ...sel, family: f }, intervals);
    return res.ok && Math.abs(res.cbv - Number(targetCbv)) < 0.005;
  });
}

// Price every candidate band and answer only if they AGREE. Two bands landing
// on one protection and one premium are the same product under two codes, and
// making a human choose between identical answers is friction that teaches them
// to click past the question that matters. Two bands that DISAGREE are a real
// question and are returned as one.
function quoteAcrossBands(families, argsFor) {
  const priced = [];
  for (const f of families || []) {
    const q = quoteRenewal(argsFor(f));
    if (q.ok) priced.push({ family: f, quote: q });
  }
  if (!priced.length) return { agree: false, options: [], reason: "NONE_PRICED" };
  const a = priced[0].quote;
  const agree = priced.every((p) =>
    Math.abs(p.quote.protection - a.protection) < 0.005
    && Math.abs(p.quote.producer_premium - a.producer_premium) < 0.005);
  return agree
    ? { agree: true, quote: a, families: priced.map((p) => p.family) }
    : { agree: false, options: priced, reason: "BANDS_DISAGREE" };
}

// ── QUOTING A GRID THE POLICY DOES NOT CARRY ──────────────────────────
//
// Kevin, 2026-09-03: "we also need ability to quote new grids the customer may
// not have been in last year." A renewal block is assembled from the AIP's own
// interval rows, so a grid with no policy behind it has no block — and without
// one the whole pricing path (quoteRenewal, the coverage ladder, the evidence
// matrix, the loss-ratio replay) has nothing to run on.
//
// `gridBlock` builds that block, and its whole job is to build it WITHOUT
// inventing the inputs a stored policy would have supplied. Every one of them
// changes the premium a grower is quoted, and none of them can be sourced from
// anywhere for ground nobody has insured yet:
//
//   grid          which 0.25-degree box              — picked
//   county        a grid straddles state and county lines, and the county base
//                 value differs between them (grid 27808: GENESEE 037 pays $330
//                 irrigated, ORLEANS 073 pays $333) — picked from what RMA
//                 publishes for this grid, never the first one in the table
//   intended use  HAYING and GRAZING price about five times apart — picked
//   acres         the multiplier on every dollar — typed
//   coverage      70-90 — picked
//   productivity  50-150% of the county base value — typed
//   allocation    which intervals and at what percent — picked
//
// So it returns `{ block, missing }` and the caller renders the block either
// way: a half-answered block still draws the county's rainfall record and the
// coverage ladder, which is most of why an agent opens this screen. What it must
// never do is PRICE one — `missing` is what the quote gates on, and a default
// anywhere in here is a premium nobody quoted on a document a grower signs.
// Same rule the wheat quoter follows on planted acres.
function gridBlock(o = {}) {
  const missing = [];
  const grid = Number(o.grid);
  if (!Number.isFinite(grid) || grid <= 0) missing.push("grid");
  const county = o.county == null || o.county === "" ? null : String(o.county);
  if (!county) missing.push("county");
  const intended_use = o.intendedUse == null || o.intendedUse === "" ? null : String(o.intendedUse);
  if (!intended_use) missing.push("intended use");

  // ⚠ THE IRRIGATION PRACTICE SETS THE COUNTY BASE VALUE — $447 non-irrigated
  // against $330 irrigated on a real book grid — so it is picked, never
  // assumed, and non-irrigated being the HIGHER tier is exactly the direction
  // somebody reasoning from "irrigated ground is worth more" gets backwards.
  //
  // Whether the grid publishes bands at all is an OBSERVATION the caller makes
  // from the actuarial table (grazing publishes one family, haying six), so it
  // is passed in rather than guessed at here. A grid publishing none needs
  // none, and requiring one would block a quote RMA is happy to write.
  const family = o.family == null || o.family === "" ? null : Number(o.family);
  if (o.bandsOffered && family == null) missing.push("irrigation practice");

  // ⚠ `Number(null)` IS 0 AND 0 IS FINITE, so every one of these tests the
  // ABSENCE explicitly before coercing. This file has already been bitten three
  // times by that (familyOf's negative band, storedBaseline's null premium).
  const numOrNull = (v) => (v == null || v === "" ? null : Number(v));
  const acres = numOrNull(o.acres);
  if (!(acres > 0)) missing.push("acres");
  const coverage_level = numOrNull(o.coverage);
  if (!(coverage_level > 0)) missing.push("coverage level");
  const productivity_factor = numOrNull(o.productivity);
  if (!(productivity_factor > 0)) missing.push("productivity factor");

  const allocation = {};
  for (const [k, v] of Object.entries(o.allocation || {})) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) allocation[k] = n;
  }
  const intervals = Object.keys(allocation).sort((a, b) => IV_ORDER.indexOf(a) - IV_ORDER.indexOf(b));
  const allocation_total = Math.round(intervals.reduce((s, l) => s + allocation[l], 0) * 1e4) / 1e4;
  // The elections must total 100%. REPORTED, never normalised — normalising
  // invents an election nobody made, which is the rule prfBlocks already holds
  // for a stored policy and there is no reason it should be looser here.
  const allocation_sums = Math.abs(allocation_total - 1) < 0.005;
  if (!intervals.length) missing.push("intervals");
  else if (!allocation_sums) missing.push("intervals totalling 100%");

  return {
    ready: missing.length === 0,
    missing,
    block: {
      // `new:` so nothing downstream can mistake this for a block the AIP wrote.
      // Keyed on the GRID alone. County and intended use are draft fields a
      // human is still moving; folding them into the identity re-keys the block
      // on every click and deselects the thing being edited.
      key: `new:${grid || "?"}`,
      is_new_grid: true,
      grid: Number.isFinite(grid) ? grid : null,
      county, intended_use, acres, coverage_level, productivity_factor,
      allocation, intervals, allocation_total, allocation_sums, family,
      label: intervals.map((l) => `${l} ${Math.round(allocation[l] * 100)}`).join(" / "),
      // There is no AIP figure to compare against, and 0 would read as "the
      // carrier wrote nothing" rather than "there is nothing to compare".
      stored_liability: null, stored_premium: null, rows: 0,
    },
  };
}

// Which counties RMA actually publishes a county base value for on this grid.
// A PRF grid is a 0.25-degree box and boxes straddle lines — 47 of 1,104
// memberships in this book do — so the county is a CHOICE with a dollar
// consequence, and offering only the one that sorts first would make it look
// settled. Codes are returned; naming them is the caller's, because the FIPS
// table lives in the app.
function gridCounties(cbvRows) {
  const seen = new Map();
  for (const r of cbvRows || []) {
    const st = String(r.state_code || ""), co = String(r.county_code || "");
    if (!st || !co) continue;
    seen.set(st + "|" + co, { state_code: st, county_code: co });
  }
  return [...seen.values()].sort((a, b) =>
    a.state_code.localeCompare(b.state_code) || a.county_code.localeCompare(b.county_code));
}

module.exports = {
  familyOf, practiceFor, bandFamilies, bandLabel, BAND_PRACTICE, typeForUse, cbvOptions, narrowCbv, cbvForElection,
  practiceChoices,
  quoteRenewal, compareYears, familiesMatchingCbv, quoteAcrossBands,
  storedBaseline, gridBlock, gridCounties,
};
